<template>
  <AuthLayout>
    <template #header>
      <h1 class="text-[32px] font-semibold">Sign in to your account</h1>
      <p class="text-strong text-xl -mt-2">Welcome back!</p>
    </template>

    <div class="w-full">
      <!-- Form inputs -->
      <div class="flex flex-col gap-3 mb-6 w-full">
        <div>
          <BaseInput
            label="Email address"
            field_name="email"
            field_type="string"
            place_holder="Enter your email"
            v-model="authStore.signin_fields.email"
            variant="secondary"
            is_required
            @keydown.enter="handleSignin"
          />
        </div>
        <div>
          <BaseInput
            label="Password"
            field_name="password"
            field_type="string"
            place_holder="Enter your password"
            v-model="authStore.signin_fields.password"
            variant="secondary"
            is_secure_field
            is_required
            @keydown.enter="handleSignin"
          />
          <div class="mt-2">
            <RouterLink to="/forgot-password" class="text-strong font-inter text-sm underline">
              Forgot/Change Password
            </RouterLink>
          </div>
          <div
            v-if="signin_error"
            class="mt-3 ml-3 font-inter text-error text-sm flex items-center"
          >
            <Icon
              name="Asterisk"
              class="mr-2 h-[14px] w-[14px] min-w-[14px] -skew-x-12 stroke-error"
            />
            {{ signin_error }}
          </div>
        </div>
      </div>

      <!-- CTA Buttons -->
      <div class="flex flex-col items-center w-full gap-3">
        <Button
          @click="handleSignin"
          @keydown.enter="handleSignin"
          size="md"
          variant="primary-grey"
          class="w-full"
        >
          <template v-if="is_loading" #label>
            <IconPulseSpinner class="h-6 w-6" />
          </template>
          <template v-else #label> Next </template>
        </Button>
        <div class="flex items-center">
          <p class="font-inter text-light">Don't have an account?</p>
          <RouterLink to="/sign-up" class="underline ml-2 font-inter font-medium">
            Sign up
          </RouterLink>
        </div>
      </div>

      <!-- Turnstile widget -->
      <div class="mt-6" data-theme="light" id="turnstile"></div>
    </div>
  </AuthLayout>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";

import { useAuthStore, useSchemaStore } from "@/stores";
import { useTurnstile } from "@/composables/useTurnstile";
import { useAnalytics } from "@/composables/useAnalytics";
import { BaseInput, Button, Icon } from "@/components";
import { IconPulseSpinner } from "@/components/icons";
import AuthLayout from "./AuthLayout.vue";

const router = useRouter();
const authStore = useAuthStore();
const schemaStore = useSchemaStore();
const { loginFailed } = useAnalytics();

const { resetTurnstile } = useTurnstile();

const signin_error = ref("");
const is_loading = ref(false);

onMounted(() => {
  window.scrollTo(0, 0);
  authStore.active_signin_step = 1;
  authStore.is_signup = false;
  authStore.mfa_channel = null;
  schemaStore.show_input_errors = false;
});

const authSchema = [
  {
    field_name: "email",
    field_type: "string",
    is_required: true,
    label: "Email",
  },
  {
    field_name: "password",
    field_type: "string",
    is_required: true,
    label: "Password",
  },
];

async function handleSignin() {
  try {
    const validateResult = schemaStore.validateFields(
      { email: authStore.signin_fields.email, password: authStore.signin_fields.password },
      authSchema,
      null,
      true
    );

    // Validate form
    if (!validateResult.proceed) {
      return;
    }

    // Validate turnstile
    if (!authStore.is_turnstile_valid) {
      signin_error.value = "Please complete the security check to confirm that you are a human.";
      return;
    }

    signin_error.value = "";
    is_loading.value = true;

    const result = await authStore.validateCredentials(
      authStore.signin_fields.email.toLowerCase().trim(),
      authStore.signin_fields.password
    );

    if (result.status === "approved") {
      authStore.signin_fields.phone = result.phone_number;
      authStore.active_signin_step = 2;
      router.push("/sign-in/auth-verification");
    } else {
      signin_error.value = `Wrong credentials, please enter the correct email and password.`;
      loginFailed({ reason: "invalid_credentials" });
      resetTurnstile();
    }
  } catch (err) {
    signin_error.value = `Wrong credentials, please enter the correct email and password.`;
    loginFailed({ reason: "error" });
    resetTurnstile();
  } finally {
    is_loading.value = false;
  }
}
</script>
